//#region [IMPORT]

// === THIRD-PARTY ===
import React,  { useEffect, useState } from 'react';
import { useParams } from 'react-router';

// === STYLE / MUI ===
import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  Box,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
} from '@mui/material';
import { Cancel, CheckCircle, UnfoldMoreOutlined } from '@mui/icons-material';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';
import {onFileRuleDocuments, removeListener, requestDocumentRules, requestFileRuleDocuments} from 'api/prokey/prokey.websocket';

//#endregion

const DOCUMENT_COMPARE_METHODS = {
  open: (a, b) => {
    return (a || 0) - (b || 0);
  },
  closed: (a, b) => {
    return (a || 0) - (b || 0);
  },
  total: (a, b) => {
    return (a || 0) - (b || 0);
  },
  status: (a, b) => {
    return (a || 0) - (b || 0);
  },
  version: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  name: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
};

//-----------------------------------------------------------------
export function DocumentView(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();
  const { file_id } = useParams();

  const [ documents   , setDocuments  ] = useState( [] )
  const [ sort_header , setSortHeader ] = useState( 'status' );
  const [ sort_ascend , setSortAscend ] = useState( true );
  const [ filters     , setFilters    ] = useState( [] );
  const [ selected_item  , setSelectedItem ] = useState( '' );
  const has_filters = filters.length > 0;
  const filtered_rules = has_filters ? documents.filter( file => filters.some( filter => filter(file) ) ) : documents;

  const _tags = [
    { label: 'Passed'      , group: 'Status' , filter: (data) => data.status === 1 },
    { label: 'Failed'      , group: 'Status' , filter: (data) => data.status === 0 },
  ];

  const [ defaultFilters , setDefaultFilters ] = useState( getFilterTags() );

  //#endregion

  //#region [STYLES]

  const _styles = {
    card: {
      height    : 'calc( 100% - 7rem )',
      marginBottom : '1rem',
      width     : '35%',
      overflowY : 'auto',
      border       : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    control_container: {
      display: 'flex',
    },
    container_filter: {
      margin: '0 0.5rem',
    },
    filter_tags: {
      width: '30rem',
    },
    table_header: {
      boxShadow : '0 0 0.5em 0.01em' + theme.palette.primary.main,
    },
    table_header_cell: {
      backgroundColor: theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_timestamp: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'timestamp' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_container: {
      height    : '100%',
      width     : '100%',
      maxWidth  : '100%',
      overflowY : 'hidden',
    },
    table_container2: {
      height    : 'calc( 100% - 5rem )',
      width     : '100%',
      maxWidth  : '100%',
      padding   : '0',
      overflowY : 'auto',
      scrollbarColor: `${theme.palette.background.secondary[theme.palette.mode]} ${theme.palette.background.primary[theme.palette.mode]}`,
    },
    task_queue_text: {
      display      : 'flex',
      flexGrow     : 1,
      margin: '1rem',
      fontSize: 'x-large',
      borderColor: 'primary',
    },
    table_cell: {
      borderBottom: "none",
      justifyContent: 'center',
      textAlign: 'center',
    },
    table_cell_name: {
      borderBottom: "none",
      // fontWeight: 'bold',
      // fontVariant: 'small-caps',
      fontKerning: 'none',
      fontSize: '100%',
    },
    table_header_status: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'status' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_open: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'open' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_closed: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'closed' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_total: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'total' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_version: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'version' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_name: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'name' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
  };

  // === ATTRIBUTES

  const CONTROL_CONTAINER = {
    sx : _styles.control_container,
  }

  const FILTER_CONTAINER = {
    sx: _styles.container_filter,
  }

  const FILTER_TAGS = {
    id           : 'filter-tags',
    sx           : _styles.filter_tags,
    multiple     : true,
    options      : _tags,
    defaultValue : defaultFilters,
    groupBy      : (option) => option.group,
    renderInput  : (params) => { return <TextField {...params} variant='standard' label="Filter Tags" /> },
    onChange     : (event, value) => {
      const selectedFilters=value.map( (x) => x.filter );  
      setFilters( selectedFilters );                                 
      saveSelectedFilters( value.map( x => x.label ) ) 
    },
  }

  const TABLE_HEADER_STATUS = {
    sx            : _styles.table_header_status,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'status' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Status</Box>
  }

  const TABLE_HEADER_OPEN = {
    sx            : _styles.table_header_open,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'open' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Open</Box>
  }

  const TABLE_HEADER_CLOSED = {
    sx            : _styles.table_header_closed,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'closed' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Closed</Box>
  }

  const TABLE_HEADER_TOTAL = {
    sx            : _styles.table_header_total,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'total' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Total</Box>
  }

  const TABLE_HEADER_VERSION = {
    sx            : _styles.table_header_version,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'version' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Version</Box>
  }

  const TABLE_HEADER_NAME = {
    sx            : _styles.table_header_name,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'name' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Document Name</Box>
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFileRuleDocuments(data) {
      //-----------------------------------------------------------------
      data.sort((a, b) => DOCUMENT_COMPARE_METHODS[sort_header](a[sort_header], b[sort_header]));
      if( !sort_ascend ) data.reverse();
      setDocuments( data );

      requestDocumentRules(file_id, selected_item);
    }

    onFileRuleDocuments( handleFileRuleDocuments );

    requestFileRuleDocuments( file_id );

    return () => {
      removeListener( 'FILE-RULE-DOCUMENTS', handleFileRuleDocuments );
    }
  }, [] );
  
  useEffect(() => {
    const tags = getFilterTags();

    setDefaultFilters( tags );
    setFilters( tags.map( tag => tag.filter ) );
  }, [documents] );

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function getFilterTags() {
    //-----------------------------------------------------------------
    let result = [];

    try {
      const saved_filters = loadSelectedFilters();
      result = _tags.filter( tag => saved_filters?.includes( tag.label ) );
    } catch (error) {
      console.warn( error );
    }
  
    return result;
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClick( numcode ) {
    //-----------------------------------------------------------------
    try {
      setSelectedItem( numcode );
      requestDocumentRules( file_id, numcode );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onDoubleClick( numcode ) {
    //-----------------------------------------------------------------
    try {
      setSelectedItem( numcode );
      requestDocumentRules( file_id, numcode );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClickHeader( name ) {
    //-----------------------------------------------------------------
    const sorted = [...documents].sort((a, b) => DOCUMENT_COMPARE_METHODS[name](a[name], b[name]));

    if( sort_header === name ) {
      setSortAscend( !sort_ascend );
      if( sort_ascend ) sorted.reverse();
    } else {
      setSortAscend( true );
      setSortHeader( name );
    }
    setDocuments(sorted);
  }

  //-----------------------------------------------------------------
  function saveSelectedFilters(filterLabels) {
    //-----------------------------------------------------------------    
    localStorage.setItem('da-document-filters', JSON.stringify(filterLabels));
  }

  //-----------------------------------------------------------------
  function loadSelectedFilters() {
    //-----------------------------------------------------------------
    const savedFilters = localStorage.getItem('da-document-filters');    
    return JSON.parse(savedFilters);
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box sx={_styles.card}>
        <TableContainer sx={_styles.table_container}>
          <Box { ...CONTROL_CONTAINER }>
            <Typography sx={_styles.task_queue_text}>
              Documents
            </Typography>
            <Box { ...FILTER_CONTAINER }>
              <Autocomplete { ...FILTER_TAGS }/>
            </Box>
          </Box>
          <TableContainer sx={_styles.table_container2}>
            <Table stickyHeader size='small'>
              <TableHead sx={_styles.table_header}>
                <TableRow>
                  <TableCell key={0} { ...TABLE_HEADER_STATUS  } />
                  <TableCell key={1} { ...TABLE_HEADER_NAME    } />
                  <TableCell key={2} { ...TABLE_HEADER_OPEN    } />
                  <TableCell key={3} { ...TABLE_HEADER_CLOSED  } />
                  <TableCell key={4} { ...TABLE_HEADER_TOTAL   } />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayPackage()}
              </TableBody>
            </Table>
          </TableContainer>
        </TableContainer>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayPackage() {
    //-----------------------------------------------------------------
    return filtered_rules.map( item => (
      <TableRow key={item.numcode} id={item.numcode}
        sx={ {
          backgroundColor: item.numcode === selected_item ? theme.palette.primary.main + getHexAlpha(60) : '',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.primary.main + getHexAlpha(40),
          },
        } }
        onClick = { () => onClick( item.numcode ) }>
        <TableCell key={0} sx={_styles.table_cell}>
          {item.status === 1 ? <CheckCircle color='success'/> : <Cancel color='error'/>}
        </TableCell>
        <TableCell key={4} sx={_styles.table_cell_name} > { item.name    } </TableCell>
        <TableCell key={1} sx={_styles.table_cell} > { item.open    } </TableCell>
        <TableCell key={2} sx={_styles.table_cell} > { item.closed  } </TableCell>
        <TableCell key={3} sx={_styles.table_cell} > { item.total   } </TableCell>
      </TableRow>
    ));
  };

  //#endregion

  return display();
}